import { RADIO_GROUP_STATE, add_radio_observer, remove_radio_observer } from './radio_state.js';
import {YalwaRadioBase} from './radio_base';
import css_text from '/srv/www/htdocs/loc/app/styles/web_components/radio_group.css';

export class YalwaRadioGroup extends YalwaRadioBase {
	static formAssociated = true;
	
	static get observedAttributes() {
		return ['intent','disabled','value','mark_required','group-id','name'];
	}
	
	constructor() {
		super();
		if (!this.shadowRoot) {
			this.attachShadow({mode: 'open'});
		}
		this._internals = this.attachInternals?.();
		this.handle_radio_observation = this.handle_radio_observation.bind(this);
	}
	
	initialize_template() {
		if (!this.shadowRoot.firstChild) {
			const template = document.createElement('template');
			template.innerHTML = `
                <div class="wrapper" part="wrapper">
                    <span class="label" part="label"><slot></slot></span>
                </div>`;
			this.shadowRoot.appendChild(template.content.cloneNode(true));
		}
		this.core_element = this.shadowRoot.querySelector('.wrapper');
	}
	
	async connectedCallback() {
		if (!this._connected_once) {
			this._connected_once = true;
			try {
				await this.load_css(css_text);          // wait for CSS
				this.initialize_template();     // then initialize template
			} catch (err) {
				console.error('Failed to load radio CSS:', err);
				this.initialize_template();     // fallback
			}
		}
		
		if (!this.hasAttribute('tabindex')) {
			this.setAttribute('tabindex', '0');
		}
		
		this.upgrade_properties(['intent','disabled','value','mark_required','group-id','name']);
		
		this.groupID = this.getAttribute('group-id');
		if (!this.groupID && this.getAttribute('name')) {
			this.groupID = `ywc-radio-${this.getAttribute('name')}-${Date.now()}`;
			this.setAttribute('group-id', this.groupID);
		}
		
		if (this.groupID) {
			add_radio_observer(this.groupID, this.handle_radio_observation);
		}
		
		if (this.hasAttribute('mark_required')) {
			this.setAttribute('required', '');
		}
		
		this.core_element?.setAttribute('tabindex','-1');
		this.handle_group_state();
		this.render();
	}
	
	disconnectedCallback() {
		if (this.groupID) {
			remove_radio_observer(this.groupID, this.handle_radio_observation);
		}
	}
	
	attributeChangedCallback(name, oldV, newV) {
		if (oldV === newV) {
			return;
		}
		if (name === 'value') {
			this.update_form_state();
		}
		if (name === 'disabled') {
			this.setAttribute('tabindex', newV !== null ? '-1' : '0');
		}
		if (name === 'mark_required') {
			if (newV !== null) {
				this.setAttribute('required', '');
			} else {
				this.removeAttribute('required');
			}
		}
		this.render();
	}
	
	render() {
		if (!this.core_element) {
			return;
		}
		this.core_element.setAttribute('data-intent', this.getAttribute('intent') || 'default');
		this.core_element.style.cursor = 'default';
	}
	
	handle_group_state() {
		const selected = RADIO_GROUP_STATE[this.groupID] ?? null;
		this._internals?.setFormValue(selected);
		if (this.mark_required && !selected) {
			this._internals?.setValidity({ valueMissing: true }, 'DEVELOPER should pre-select one of the options!', this);
			this.setAttribute('data-validation-error','true');
		} else {
			this._internals?.setValidity({});
			this.removeAttribute('data-validation-error');
		}
		this._internals?.setFormValue(selected);
	}
	
	handle_radio_observation({ selected_value }) {
		this.handle_group_state();
		this.dispatchEvent(new CustomEvent('change', { detail: { value: selected_value }, bubbles: true }));
	}
	
	// --- props
	get value() {
		return RADIO_GROUP_STATE[this.groupID] ?? null;
	}
	set value(v) {
		this.setAttribute('value', v);
	}
	
	update_form_state() {
		this._internals?.setFormValue(RADIO_GROUP_STATE[this.groupID] ?? null);
	}
	
	checkValidity() {
		return this._internals?.checkValidity() ?? false;
	}
	reportValidity() {
		return this._internals?.reportValidity() ?? false;
	}
	get form() {
		return this._internals?.form ?? null;
	}
}

if (!customElements.get('yalwa-radio-group')) {
	customElements.define('yalwa-radio-group', YalwaRadioGroup);
}